import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Trophy,
  Clock,
  BarChart2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const VisualSearchMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="search-task"
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "search-task", name: "Visual Search Task" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={VisualSearchMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const VisualSearchMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [difficulty, setDifficulty] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown states
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Game specific states
  const [currentGrid, setCurrentGrid] = useState([]);
  const [currentResponseRecorded, setCurrentResponseRecorded] = useState(false);
  const [stimulusDisplayTime, setStimulusDisplayTime] = useState(null);
  const trialTimeoutRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);

  // Scoring and stats
  const [score, setScore] = useState(0);
  const [hitCount, setHitCount] = useState(0);
  const [commissionErrors, setCommissionErrors] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [hitStreak, setHitStreak] = useState(0);

  // Theme colors
  const primaryColor = "from-amber-500 to-orange-600";
  const primaryColorSolid = "bg-amber-500";
  const secondaryColor = "text-amber-300";

  // Constants
  const GAME_DURATION = 60; // seconds
  const STIMULUS_INTERVAL = 2500; // ms per grid

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // Update isPlayingRef when isPlaying changes
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (trialTimeoutRef.current) clearTimeout(trialTimeoutRef.current);
    };
  }, [isPlaying]);

  // Start the game with countdown
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setHitCount(0);
    setHitStreak(0);
    setCommissionErrors(0);
    setMissedTargets(0);
    setReactionTimes([]);
    setTimeLeft(GAME_DURATION);
    setIsPlaying(true);

    // Get difficulty from gameState if available
    if (gameState && gameState.gameDifficulty) {
      setDifficulty(gameState.gameDifficulty);
    }

    // Clear any previous trial timeouts
    if (trialTimeoutRef.current) clearTimeout(trialTimeoutRef.current);

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }

    // Begin with the first trial almost immediately
    setTimeout(() => {
      showNextTrial();
    }, 100);
  };

  // Generate a grid for the current trial
  const generateGrid = useCallback(() => {
    const gridSize = difficulty + 2;
    const totalCells = gridSize * gridSize;

    // Set target probability (target becomes less frequent as difficulty increases)
    let targetProbability;
    if (difficulty === 1) targetProbability = 0.8;
    else if (difficulty === 2) targetProbability = 0.6;
    else targetProbability = 0.4;

    const hasTarget = Math.random() < targetProbability;
    const targetIndex = hasTarget
      ? Math.floor(Math.random() * totalCells)
      : null;

    const grid = [];
    for (let i = 0; i < totalCells; i++) {
      grid.push({ id: i, isTarget: hasTarget && i === targetIndex });
    }
    return grid;
  }, [difficulty]);

  // Show the next trial (grid)
  const showNextTrial = () => {
    // Check previous trial: if target was present but no response was made, count as missed target.
    if (currentGrid.length > 0 && !currentResponseRecorded) {
      const previousHasTarget = currentGrid.some((cell) => cell.isTarget);
      if (previousHasTarget) {
        setMissedTargets((prev) => prev + 1);
        setScore((prev) => Math.max(0, prev - 3)); // Missed target penalty
        setHitStreak(0); // Reset streak

        // Update game state
        if (updateGameState) {
          updateGameState({
            myProgress: hitCount / 30, // Assuming 30 targets is a complete game
            myScore: Math.max(0, score - 3),
            myLastResponse: {
              correct: false,
              points: -3,
              missedTarget: true,
            },
          });
        }
      }
    }

    // End trial if game is no longer playing
    if (!isPlayingRef.current) return;

    // Generate a new grid and set it as current trial
    const grid = generateGrid();
    setCurrentGrid(grid);
    setCurrentResponseRecorded(false);
    setStimulusDisplayTime(Date.now());

    // Schedule next trial
    trialTimeoutRef.current = setTimeout(() => {
      showNextTrial();
    }, STIMULUS_INTERVAL);
  };

  // Handle cell click
  const handleCellClick = (cell) => {
    if (!isPlayingRef.current || currentResponseRecorded) return;

    const responseTime = Date.now() - stimulusDisplayTime;
    setCurrentResponseRecorded(true);

    if (cell.isTarget) {
      // Base points for correct hit
      let basePoints = 20;

      // Reaction time bonus
      let rtBonus = 0;
      if (responseTime < 600) rtBonus = 10;
      else if (responseTime < 1000) rtBonus = 5;

      // Combo bonus
      const newStreak = hitStreak + 1;
      let comboBonus = 0;
      if (newStreak % 10 === 0) comboBonus = 25;
      else if (newStreak % 5 === 0) comboBonus = 10;
      else if (newStreak % 3 === 0) comboBonus = 5;

      const totalPoints = basePoints + rtBonus + comboBonus;

      setScore((prev) => prev + totalPoints);
      setHitCount((prev) => prev + 1);
      setHitStreak(newStreak);
      setReactionTimes((prev) => [...prev, responseTime]);

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: (hitCount + 1) / 30, // Assuming 30 hits is a complete game
          myScore: score + totalPoints,
          myLastResponse: {
            correct: true,
            points: totalPoints,
            responseTime,
            streak: newStreak,
          },
        });
      }
    } else {
      // Commission error
      setCommissionErrors((prev) => prev + 1);
      setScore((prev) => Math.max(0, prev - 10));
      setHitStreak(0); // Reset streak on error

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: hitCount / 30,
          myScore: Math.max(0, score - 10),
          myLastResponse: {
            correct: false,
            points: -10,
            responseTime,
            commissionError: true,
          },
        });
      }
    }
  };

  // End the game
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    // Clean up timers
    clearInterval(timerRef.current);
    clearTimeout(trialTimeoutRef.current);

    setIsPlaying(false);
    setGameMode("results");

    // Calculate RT variability
    const rtVariability = calculateRTVariability();

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        hitCount,
        commissionErrors,
        missedTargets,
        averageReactionTime:
          reactionTimes.length > 0
            ? Math.round(
                reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
              )
            : 0,
        reactionTimeVariability: rtVariability,
        accuracy: hitCount > 0 ? hitCount / (hitCount + commissionErrors) : 0,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Calculate Reaction Time Variability
  const calculateRTVariability = () => {
    if (reactionTimes.length < 2) return null;

    const mean =
      reactionTimes.reduce((acc, rt) => acc + rt, 0) / reactionTimes.length;
    const variance =
      reactionTimes.reduce((acc, rt) => acc + Math.pow(rt - mean, 2), 0) /
      (reactionTimes.length - 1);

    return Math.round(Math.sqrt(variance));
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  useEffect(() => {
    // Check if the opponent has quit
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // Determine grid dimensions based on difficulty
  const gridSize = difficulty + 2;

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div
            className={`font-medium flex items-center gap-2 ${secondaryColor}`}
          >
            <Brain size={18} className="text-amber-400" />
            <span>Visual Seeker Challenge</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {/* Start button or waiting indicator */}
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  {waitingForOpponent ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 border-4 border-t-transparent border-amber-500 rounded-full animate-spin"></div>
                      <p className="text-white text-xl">
                        Waiting for opponent...
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleReadyClick}
                      className="py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg"
                    >
                      Ready to Play
                    </button>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              {showCountdown && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl font-bold text-white mb-4">
                      {countdown > 0 ? countdown : "GO!"}
                    </div>
                    <div className="text-xl text-slate-300">Get ready...</div>
                  </div>
                </div>
              )}

              {/* Game stats header */}
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="ml-4 bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Opponent progress */}
              {gameState && opponent && (
                <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-300">
                      {opponent.name}'s Progress
                    </span>
                    <span className="text-xs text-white">
                      {gameState?.progress && gameState.progress[opponent.email]
                        ? `${Math.round(
                            gameState.progress[opponent.email] * 100
                          )}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="bg-amber-500 h-full rounded-full"
                      style={{
                        width: `${
                          ((gameState?.progress &&
                            gameState.progress[opponent.email]) ||
                            0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  {gameState?.scores &&
                    gameState.scores[opponent.email] !== undefined && (
                      <div className="mt-1 text-right text-xs text-amber-300">
                        Score: {gameState.scores[opponent.email]}
                      </div>
                    )}
                </div>
              )}

              {/* Game content */}
              <div className="flex flex-col items-center justify-center">
                <div className="mb-4 text-center">
                  <h3 className="text-white font-medium mb-2">
                    Find and click the unique target (🔶) quickly!
                  </h3>
                  <div className="flex gap-2 items-center justify-center">
                    <span className="inline-flex items-center gap-1 text-sm text-slate-300">
                      <span className="text-green-400 font-medium">Hits:</span>{" "}
                      {hitCount}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm text-slate-300">
                      <span className="text-red-400 font-medium">Errors:</span>{" "}
                      {commissionErrors}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm text-slate-300">
                      <span className="text-amber-300 font-medium">
                        Streak:
                      </span>{" "}
                      {hitStreak}
                    </span>
                  </div>
                </div>

                {/* Grid Display */}
                <div className="mb-8">
                  {currentGrid && currentGrid.length > 0 && (
                    <div
                      className="grid gap-2"
                      style={{
                        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                      }}
                    >
                      {currentGrid.map((cell) => (
                        <button
                          key={cell.id}
                          onClick={() => handleCellClick(cell)}
                          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center text-2xl font-bold bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                        >
                          {cell.isTarget ? "🔶" : "🔷"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Instructions reminder */}
                <div className="mt-2 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 max-w-md text-center">
                  Click the target (🔶) when you see it as quickly as you can.
                  Avoid clicking on distractors (🔷).
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-amber-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-amber-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-amber-300">
                    Visual Search
                  </span>
                  : Tests your ability to quickly find important information in
                  a cluttered visual field.
                  <br />
                  <span className="font-medium text-amber-300">
                    Reaction Time
                  </span>
                  : How quickly you can detect and respond to targets.
                  <br />
                  <span className="font-medium text-amber-300">
                    RT Variability
                  </span>
                  : Lower variability indicates more consistent attention and
                  focus.
                </p>
              </div>

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-amber-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-amber-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        Level {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Hits</span>
                      <span className="text-green-400">{hitCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Commission Errors</span>
                      <span className="text-red-400">{commissionErrors}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Targets</span>
                      <span className="text-amber-400">{missedTargets}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-amber-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Accuracy bar */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {hitCount + commissionErrors > 0
                            ? `${Math.round(
                                (hitCount / (hitCount + commissionErrors)) * 100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-600"
                          style={{
                            width:
                              hitCount + commissionErrors > 0
                                ? `${
                                    (hitCount / (hitCount + commissionErrors)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Average Response Time</span>
                      <span>
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Reaction Time Variability</span>
                      <span>
                        {calculateRTVariability() !== null
                          ? `${calculateRTVariability()} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Visual search abilities are crucial for many daily
                        tasks. Lower reaction times and higher accuracy indicate
                        improved attention focus and visual processing speed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualSearchMultiWrapper;
